import React, { useRef, useMemo, useEffect, useCallback, useLayoutEffect, useState, } from 'react';
import Animated, { runOnJS, withTiming, interpolate, useSharedValue, interpolateColor, useAnimatedStyle, useAnimatedScrollHandler, } from 'react-native-reanimated';
import { View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import StatusBar from 'design-system/StatusBar';
import ListLoader from 'design-system/ListLoader';
import FakeSearchBar from 'design-system/FakeSearchBar';
import ListEmptyView from 'design-system/ListEmptyView';
import { AppView, NavigationHeader, SettingsItem, SettingsSection, } from 'design-system';
import SearchBar from 'design-system/SearchBar';
import { rem, SCREEN_BOTTOM_PADDING } from 'design-system/values';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import FlatListItemCard from 'design-system/FlatListItemCard';
import { useAppSelector } from 'store/hooks';
import { isWeb } from '../../../constants';
import { useHelpArticlesSearch } from '../hooks/useHelpArticlesSearch';
import RecommendedArticles from '../components/RecommendedArticles';
import { selectIsFetchingCategory } from '../selectors';
import SearchHelpEmptyComponent from '../components/HelpSearchEmptyComponent';
import { useHelpArticlesConfig } from '../hooks/useHelpArticlesConfig';
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const keyExtractor = (item) => item.article_id.toString();
const SearchHelpArticles = ({ route, navigation, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { searchBarY, articlesKey } = route.params;
    const { omitFaq, initialArticles, relatedArticlesTitle: recommendedArticlesTitle, } = useHelpArticlesConfig(articlesKey);
    const { top } = useSafeAreaInsets();
    const offset = (searchBarY || 0) - rem(8);
    const scrollY = useSharedValue(0);
    const searchBarRef = useRef(null);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: isWeb,
        });
    }, []);
    const progressSharedValue = useSharedValue(isWeb ? 1 : 0);
    const focusSearch = useCallback(() => {
        searchBarRef.current?.focus();
    }, []);
    useEffect(() => {
        if (!isWeb) {
            progressSharedValue.value = withTiming(1, undefined, () => {
                runOnJS(focusSearch)();
            });
        }
        else {
            focusSearch();
        }
    }, [focusSearch, progressSharedValue]);
    const textInputStyle = useAnimatedStyle(() => ({
        backgroundColor: interpolateColor(scrollY.value, [0, 40], [
            colors.cards.onDark,
            colors.background.dark,
        ]),
    }), [colors, scrollY]);
    const fakeSearchBarStyles = useAnimatedStyle(() => ({
        transform: [
            {
                translateY: interpolate(progressSharedValue.value, [0, 1], [offset, top], 'clamp'),
            },
        ],
        opacity: progressSharedValue.value === 1 ? 0 : 1,
    }), [offset, progressSharedValue, top]);
    const handleCancel = useCallback(() => {
        if (!isWeb) {
            progressSharedValue.value = withTiming(0, {
                duration: 200,
            }, () => {
                runOnJS(navigation.pop)();
            });
        }
        else {
            navigation.pop();
        }
    }, [progressSharedValue, navigation]);
    const listAnimatedStyle = useAnimatedStyle(() => ({
        opacity: interpolate(progressSharedValue.value, [0.5, 1], [0, 1], 'clamp'),
    }));
    const searchBarAnimatedStyle = useAnimatedStyle(() => ({
        opacity: progressSharedValue.value === 1 ? 1 : 0,
    }));
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const isFetchingCategory = useAppSelector(selectIsFetchingCategory);
    const { searchByTitle, helpArticles, isFetching, openHelpArticle } = useHelpArticlesSearch();
    const [searchTermLength, setSearchTermLength] = useState(0);
    const onChangeSearchTerm = useCallback((searchTerm) => {
        searchByTitle(searchTerm);
        setSearchTermLength(searchTerm.length);
    }, [searchByTitle]);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle, styles.contentContainerStyle);
    const renderItem = useCallback(({ item, index }) => (<FlatListItemCard index={index} itemsLength={helpArticles.length} style={styles.card}>
        <SettingsItem key={item.article_id} title={item.title} onPress={() => item.url && openHelpArticle(item.url)}/>
      </FlatListItemCard>), [helpArticles.length, openHelpArticle, styles.card]);
    const onScroll = useAnimatedScrollHandler((e) => {
        scrollY.value = e.contentOffset.y;
    });
    const HeaderComponent = useMemo(() => helpArticles.length ? (<SettingsSection noMarginTop name={'Results'}/>) : null, [helpArticles.length]);
    const EmptyComponent = useMemo(() => {
        if (isFetching || isFetchingCategory) {
            return <ListLoader smallMargin/>;
        }
        if (!searchTermLength && !initialArticles?.length && omitFaq) {
            return <SearchHelpEmptyComponent />;
        }
        if (!searchTermLength) {
            return (<RecommendedArticles headerTitle={recommendedArticlesTitle ?? 'Related topics'} articles={initialArticles} noMarginTop omitFaq={omitFaq}/>);
        }
        return (<ListEmptyView alt onCard happy title="No results found" subTitle="Sorry, we couldn't find an answer. Try searching with different keywords, or chat with us" style={styles.emptyView}/>);
    }, [
        initialArticles,
        isFetching,
        isFetchingCategory,
        omitFaq,
        recommendedArticlesTitle,
        searchTermLength,
        styles.emptyView,
    ]);
    const Content = useMemo(() => {
        const baseProps = {
            onScroll,
            renderItem,
            keyExtractor,
            contentContainerStyle,
            scrollEventThrottle: 16,
            ListHeaderComponent: HeaderComponent,
            data: helpArticles,
            ListEmptyComponent: EmptyComponent,
            keyboardDismissMode: 'on-drag',
            keyboardShouldPersistTaps: 'handled',
        };
        if (isWeb) {
            return <Animated.FlatList {...baseProps}/>;
        }
        return <AnimatedFlashList {...baseProps}/>;
    }, [
        EmptyComponent,
        HeaderComponent,
        contentContainerStyle,
        helpArticles,
        onScroll,
        renderItem,
    ]);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <View style={styles.flex}>
        <Animated.View style={searchBarAnimatedStyle}>
          <NavigationHeader scrollY={scrollY} noPaddingTop>
            <View style={paddingHorizontalStyle}>
              <SearchBar alwaysShowCancel ref={searchBarRef} onCancel={handleCancel} placeholder="Search" containerStyle={styles.searchBar} textInputBgStyles={textInputStyle} onChangeSearchTerm={onChangeSearchTerm} debounceTime={500}/>
            </View>
          </NavigationHeader>
        </Animated.View>
        <Animated.View style={[styles.flex, listAnimatedStyle]}>
          {Content}
        </Animated.View>
      </View>
      {!isWeb && (<Animated.View style={[
                styles.fakeSearchBar,
                fakeSearchBarStyles,
                paddingHorizontalStyle,
            ]} pointerEvents="none">
          <FakeSearchBar placeholder="Search"/>
        </Animated.View>)}
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    content: {
        paddingTop: rem(16),
    },
    indicator: {
        paddingBottom: rem(200),
    },
    searchBar: {
        marginTop: rem(isWeb ? 24 : 8),
        paddingTop: rem(8),
    },
    contentContainerStyle: {
        paddingTop: rem(16),
    },
    fakeSearchBar: {
        left: 0,
        right: 0,
        marginTop: rem(8),
        position: 'absolute',
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
    emptyView: {
        marginTop: rem(8),
    },
}));
export default SearchHelpArticles;
